import { DOMHelpers } from "helpers/dom";
import { Subscribable, SubscribableOrNullableValue } from "knockout";
import { withEffects } from "mixins/withEffects";
import { WCCSliderAutoScrollMode } from "../enums/autoScroll";
import WCCSliderPage from "../models/page";
import { WCCSliderAutoScrollType } from "../types/autoscrollType";

export interface WCCSliderAutoScrollManagerConfig<T> extends WCCSliderAutoScrollManagerConfigPublic {
    ready: SubscribableOrNullableValue<boolean>
    hasNextPage: SubscribableOrNullableValue<boolean>
    hasMultiplePages: SubscribableOrNullableValue<boolean>
    isMouseInsideSlider: SubscribableOrNullableValue<boolean>
    activePage: SubscribableOrNullableValue<WCCSliderPage<T>>
    defaultPage: SubscribableOrNullableValue<WCCSliderPage<T>>

    openNextPage: Action
    selectPage: Action<[page: WCCSliderPage<T>]>
}

export interface WCCSliderAutoScrollManagerConfigPublic {
    type?: SubscribableOrNullableValue<WCCSliderAutoScrollType>
    delay?: SubscribableOrNullableValue<number>
}

export default class WCCSliderAutoScrollManager<T> {
    private effects = withEffects();

    private _isWindowFocused = ko.observable(true);
    private animationFrameId = ko.observable<number>();
    private animationPrevFrameTime = ko.observable<number>();

    private mode: Subscribable<WCCSliderAutoScrollMode>
    private delay: Subscribable<number>
    private animationDuration: Subscribable<number>

    private activePage: Subscribable<WCCSliderPage<T> | undefined>
    private defaultPage: Subscribable<WCCSliderPage<T> | undefined>

    private isReady: Subscribable<boolean>
    private hasNextPage: Subscribable<boolean>
    private hasMultiplePages: Subscribable<boolean>
    private isAnimationNeeded: Subscribable<boolean>

    progress = ko.observable(0);

    isEnabled: Subscribable<boolean>

    constructor(private config: WCCSliderAutoScrollManagerConfig<T>) {
        const type = ko.flattenComputed(config.type);
        this.mode = ko.pureComputed(() => this.getAutoScrollMode(type()));

        this.delay = ko.flattenComputed(config.delay, 10);               

        this.isReady = ko.flattenComputed(config.ready, true);
        this.hasNextPage = ko.flattenComputed(config.hasNextPage, false);
        this.hasMultiplePages = ko.flattenComputed(config.hasMultiplePages, false);
        const isMouseInsideSlider = ko.flattenComputed(config.isMouseInsideSlider, false);

        this.activePage = ko.flattenComputed(config.activePage);
        this.defaultPage = ko.flattenComputed(config.defaultPage);

        this.isEnabled = ko.pureComputed(() => this.checkIfShouldEnable());
        const isPauseRequired = ko.pureComputed(() => isMouseInsideSlider() || !this._isWindowFocused());
        
        this.animationDuration = ko.pureComputed(() => this.getAnimationDuration());

        this.isAnimationNeeded = ko.pureComputed(() => this.isReady() && this.isEnabled() && !isPauseRequired() && this.progress() < 100);

        queueMicrotask(() => {
            this.effects.register([
                DOMHelpers.on(window, 'focus', () => this._isWindowFocused(true)),
                DOMHelpers.on(window, 'blur', () => this._isWindowFocused(false)),

                this.progress.subscribe(progress => this.onProgress(progress)),
                this.activePage.subscribe(() => this.onPageChange()),

                { dispose: () => this.cancelAnimation() }
            ]);            

            this.effects.register(animationNeeded => {
                if (animationNeeded) {
                    if (this.animationFrameId() == null)
                        this.animationFrameId(requestAnimationFrame(this.animate.bind(this)));
                } else {
                    this.cancelAnimation();
                }
            }, [this.isAnimationNeeded]);
        });
    }

    dispose() {
        this.effects.dispose();
    }

    private checkIfShouldEnable() {
        if (this.delay() > 0) {
            switch (this.mode()) {
                case WCCSliderAutoScrollMode.disabled: return false;
                case WCCSliderAutoScrollMode.single: return this.hasNextPage();
                case WCCSliderAutoScrollMode.loop: return this.hasMultiplePages();
            }
        }

        return false;
    }

    private getAnimationDuration() {
        return this.delay();
    }

    private animate(now: number) {
        const animationPrevFrameTime = this.animationPrevFrameTime();

        if (animationPrevFrameTime != undefined) {
            const start = animationPrevFrameTime;
            const change = (now - start) / 1000;

            let progress = this.progress() + change / this.animationDuration() * 100;

            if (progress > 100)
                progress = 100;

            this.progress(progress);
        }

        if (this.isAnimationNeeded()) {
            this.animationPrevFrameTime(now);
            this.animationFrameId(requestAnimationFrame(this.animate.bind(this)));
        } else {
            this.cancelAnimation();
        }
    }

    private reset() {
        this.progress(0);
    }

    private cancelAnimation() {
        const animationFrameId = this.animationFrameId();

        if (animationFrameId != undefined) {
            window.cancelAnimationFrame(animationFrameId);
            this.animationFrameId(undefined);
            this.animationPrevFrameTime(undefined);
        }
    }

    private onProgress(value: number) {
        if (value === 100) {
            if (this.hasNextPage()) {
                this.config.openNextPage();
            } else {
                const defaultPage = this.defaultPage();

                if (defaultPage != undefined)
                    this.config.selectPage(defaultPage);
            }

            _.defer(() => this.reset());
        }
    }

    private onPageChange() {
        this.reset();
    }

    private getAutoScrollMode(type?: WCCSliderAutoScrollType) {
        switch (type) {
            case 'single': return WCCSliderAutoScrollMode.single;
            case 'loop': return WCCSliderAutoScrollMode.loop;
            default: return WCCSliderAutoScrollMode.disabled;
        }
    }
}